import { useEffect, useRef, useState } from 'react';

import { debug } from '@/shared/lib';

import type {
  FullHeadMprSliceRecordViewsByTileID,
  FullHeadMprSliceTileView,
} from '../config';
import { applyFullHeadMprSliceViewOptionsToPngUrl } from '../lib';

const makeSignature = (
  tile: FullHeadMprSliceTileView,
  record: FullHeadMprSliceRecordViewsByTileID[string],
) =>
  [
    tile.pngUrl,
    record.viewOptions.brightness,
    record.viewOptions.contrast,
    record.viewOptions.sharpness,
    record.viewOptions.invert,
  ].join('|');

export const useFullHeadMprSliceTileSrcs = ({
  recordsByTileID,
  tiles,
}: {
  recordsByTileID: FullHeadMprSliceRecordViewsByTileID;
  tiles: FullHeadMprSliceTileView[];
}): {
  isTileSrcSettled: (tile: FullHeadMprSliceTileView) => boolean;
  resolveTileSrc: (tile: FullHeadMprSliceTileView) => string;
} => {
  const [srcByTileID, setSrcByTileID] = useState<
    Record<string, { signature: string; src: string }>
  >({});
  const signatureByTileID = useRef<Record<string, string>>({});

  useEffect(() => {
    tiles.forEach((tile) => {
      const record = recordsByTileID[tile.id];

      if (!record?.hasViewOptions || !tile.pngUrl) {
        return;
      }

      const signature = makeSignature(tile, record);

      if (signatureByTileID.current[tile.id] === signature) {
        return;
      }

      signatureByTileID.current[tile.id] = signature;

      // NOTE: superseded bakes are rejected by the signature check — an isStale
      // cleanup flag would also drop a bake that outlives one effect run (any tooth
      // update re-runs the effect, which skips the tile as already-signed), leaving
      // the tile raw and the print settle gate stuck.
      applyFullHeadMprSliceViewOptionsToPngUrl(tile.pngUrl, record.viewOptions)
        .then((adjustedSrc) => {
          if (signatureByTileID.current[tile.id] !== signature) {
            return;
          }

          setSrcByTileID((current) => ({
            ...current,
            [tile.id]: { signature, src: adjustedSrc },
          }));
        })
        .catch((error) => {
          debug('[FullHeadMPR] tile view options apply failed', tile.id, error);

          if (signatureByTileID.current[tile.id] !== signature) {
            return;
          }

          // NOTE: a failed bake settles the tile with its raw src — degrading the
          // picture, not deadlocking typewriterReadyToPDF, which waits for every
          // selected tile to settle.
          setSrcByTileID((current) => ({
            ...current,
            [tile.id]: { signature, src: tile.pngUrl },
          }));
        });
    });
  }, [tiles, recordsByTileID]);

  const resolveTileSrc = (tile: FullHeadMprSliceTileView) => {
    const record = recordsByTileID[tile.id];

    if (!record?.hasViewOptions) {
      return tile.pngUrl;
    }

    const rendered = srcByTileID[tile.id];
    return rendered?.signature === makeSignature(tile, record)
      ? rendered.src
      : tile.pngUrl;
  };

  const isTileSrcSettled = (tile: FullHeadMprSliceTileView) => {
    const record = recordsByTileID[tile.id];
    return (
      !record?.hasViewOptions ||
      srcByTileID[tile.id]?.signature === makeSignature(tile, record)
    );
  };

  return { isTileSrcSettled, resolveTileSrc };
};
